import { useLocation } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import { useI18n } from '../../i18n/LanguageContext';
import { resolveLandingContext } from './regional';

type PageContent = {
  title: string;
  subtitle: string;
  sectionTitle: string;
  sectionBody: string;
  sectionDetail: string;
  imageAlt: string;
  benefitsTitle: string;
  benefits: Array<{ title: string; body: string }>;
};

const CONTENT: Record<'default' | 'es', PageContent> = {
  default: {
    title: 'Clinical Triage AI',
    subtitle: 'Automated patient prioritization to reduce waiting times and improve operational safety.',
    sectionTitle: 'Real-Time Risk Classification',
    sectionBody: 'The system scores urgency, flags red-alert signals, and suggests next actions for intake and care teams.',
    sectionDetail: 'This improves decision consistency during high-volume periods.',
    imageAlt: 'Urgent care queue with AI triage',
    benefitsTitle: 'Benefits for Urgent-Care Operations',
    benefits: [
      { title: 'Fewer Bottlenecks', body: 'Queues are sorted by actual risk, not only arrival order.' },
      { title: 'Response-Time Control', body: 'Immediate visibility into patients requiring early intervention.' },
      { title: 'Clinical Standardization', body: 'More consistent triage protocols across shifts and locations.' },
    ],
  },
  es: {
    title: 'Triage Clinico con IA en Espanol',
    subtitle: 'Priorizacion automatica de pacientes para redes asistenciales con alta demanda operativa y equipos de trabajo en espanol.',
    sectionTitle: 'Clasificacion de Riesgo en Tiempo Real',
    sectionBody: 'El sistema puntua urgencia, detecta alertas clinicas y recomienda acciones para admision, enfermeria y equipos medicos.',
    sectionDetail: 'Esto mejora consistencia de decisiones y reduce tiempos de espera en picos de demanda.',
    imageAlt: 'Cola de pacientes con triage IA en espanol',
    benefitsTitle: 'Impacto en Operacion de Urgencias',
    benefits: [
      { title: 'Menos Cuellos de Botella', body: 'La cola se ordena por riesgo clinico y prioridad real de atencion.' },
      { title: 'Control de Tiempos', body: 'Visibilidad inmediata de pacientes que requieren intervencion temprana.' },
      { title: 'Estandarizacion Asistencial', body: 'Criterios de triage consistentes entre turnos y sedes.' },
    ],
  },
};

export default function TriageAiPage() {
  const location = useLocation();
  const { language } = useI18n();
  const { market } = resolveLandingContext(location.pathname, language);
  const content = CONTENT[market] ?? CONTENT.default;

  return (
    <LandingLayout pageKey="triage-ai">
      <section className="tech-header">
        <div className="container">
          <h1 className="reveal-text">{content.title}</h1>
          <p className="tech-features-intro">{content.subtitle}</p>
        </div>
      </section>

      <section className="deep-dive">
        <div className="container deep-dive-grid">
          <div className="dd-content">
            <h2>{content.sectionTitle}</h2>
            <p>{content.sectionBody}</p>
            <p className="tech-feature-detail">{content.sectionDetail}</p>
          </div>
          <div className="dd-image">
            <img src="/emr/walkin-urgent-care-ai-assisted.png" alt={content.imageAlt} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <h2>{content.benefitsTitle}</h2>
          <div className="features-grid">
            {content.benefits.map((item) => (
              <div key={item.title} className="feature-item">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
